import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import './index.css';
import App from './App';
import { AuthProvider, useAuth } from './AuthContext';
import { Spinner } from './components/common';

import Login from './pages/Login';
import CalendarBoard from './pages/CalendarBoard';
import Orders from './pages/Orders';
import Cashier from './pages/Cashier';
import StaffRoster from './pages/StaffRoster';
import Commission from './pages/Commission';
import Report from './pages/Report';
import Customers from './pages/Customers';
import CustomerDetail from './pages/CustomerDetail';
import BookingBuilder from './pages/BookingBuilder';
import SalePage from './pages/SalePage';
import BookingWebsite from './pages/BookingWebsite';
import Profile from './pages/Profile';
import SpaHome from './public/SpaHome';
import SpaBooking from './public/SpaBooking';
import SpaBookingPayment from './public/SpaBookingPayment';
import {
  SpaContact,
  SpaFacilities,
  SpaMassage,
  SpaServices,
  SpaStory,
} from './public/SpaPages';
import {
  SettingGeneral, SettingStore, SettingProduct, SettingPromotion,
  SettingPackage, SettingStaff, SettingRoom, SettingCustomer,
  SettingBill, SettingBookingType, SettingRole,
} from './pages/Settings';
import reportWebVitals from './reportWebVitals';

const SITE_MODE = process.env.REACT_APP_SITE_MODE === 'public' ? 'public' : 'pos';

/**
 * Holds the shell back until the session has been rehydrated, otherwise a
 * reload bounces a signed-in user to /login before /api/users/me answers.
 */
function RequireAuth({ children }) {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <Spinner />;
  return isAuthenticated ? children : <Navigate to="/login" replace />;
}

/**
 * React Router changes pages without a browser document navigation, so the
 * browser otherwise carries the old page's scroll position into the next one.
 * Reset only when the route/search changes; hash-only links still scroll to
 * their intended section through the public site's own anchor handling.
 */
function ScrollToTop() {
  const { pathname, search } = useLocation();

  React.useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [pathname, search]);

  return null;
}

function PublicApplication() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <Routes>
        {/* Clean production URLs. The /spa aliases preserve old bookmarks. */}
        <Route path="/" element={<SpaHome />} />
        <Route path="/story" element={<SpaStory />} />
        <Route path="/massage" element={<SpaMassage />} />
        <Route path="/services" element={<SpaServices />} />
        <Route path="/facilities" element={<SpaFacilities />} />
        <Route path="/contact" element={<SpaContact />} />
        <Route path="/book" element={<SpaBooking />} />
        <Route path="/booking/payment" element={<SpaBookingPayment />} />
        <Route path="/spa" element={<SpaHome />} />
        <Route path="/spa/story" element={<SpaStory />} />
        <Route path="/spa/massage" element={<SpaMassage />} />
        <Route path="/spa/services" element={<SpaServices />} />
        <Route path="/spa/facilities" element={<SpaFacilities />} />
        <Route path="/spa/contact" element={<SpaContact />} />
        <Route path="/spa/book" element={<SpaBooking />} />
        <Route path="/spa/booking/payment" element={<SpaBookingPayment />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

function PosApplication() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route
            element={
              <RequireAuth>
                <App />
              </RequireAuth>
            }
          >
            <Route index element={<CalendarBoard />} />
            <Route path="/history" element={<Orders />} />
            <Route path="/cashier" element={<Cashier />} />
            <Route path="/staff" element={<StaffRoster />} />
            <Route path="/commission" element={<Commission />} />
            <Route path="/report" element={<Report />} />
            <Route path="/customers" element={<Customers />} />
            <Route path="/customers/:id" element={<CustomerDetail />} />
            <Route path="/booking-website" element={<BookingWebsite />} />
            <Route path="/book" element={<BookingBuilder />} />
            <Route path="/buy-product" element={<SalePage mode="product" />} />
            <Route path="/buy-package" element={<SalePage mode="package" />} />
            <Route path="/setting-profile" element={<Profile />} />
            <Route path="/setting-general" element={<SettingGeneral />} />
            <Route path="/setting-store" element={<SettingStore />} />
            <Route path="/setting-product" element={<SettingProduct />} />
            <Route path="/setting-promotion" element={<SettingPromotion />} />
            <Route path="/setting-package" element={<SettingPackage />} />
            <Route path="/setting-staff" element={<SettingStaff />} />
            <Route path="/setting-room" element={<SettingRoom />} />
            <Route path="/setting-customer" element={<SettingCustomer />} />
            <Route path="/setting-bill" element={<SettingBill />} />
            <Route path="/setting-booking-type" element={<SettingBookingType />} />
            <Route path="/setting-role" element={<SettingRole />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    {SITE_MODE === 'public' ? <PublicApplication /> : <PosApplication />}
  </React.StrictMode>
);

reportWebVitals();
